import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BlogLayout from "@/components/BlogLayout";
import GhostSubscribeForm from "@/components/GhostSubscribeForm";
import useSEO from "@/hooks/useSEO";
import { getPosts, getTags, formatDate } from "@/lib/ghost";

function PostCard({ post }) {
  const tag = post.tags?.[0];
  return (
    <article className="border border-border rounded-lg bg-card overflow-hidden hover:border-brand/40 transition-colors group flex flex-col">
      {post.feature_image && (
        <Link to={`/blog/${post.slug}`} className="block overflow-hidden">
          <img
            src={post.feature_image}
            alt={post.title}
            className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        </Link>
      )}
      <div className="p-6 flex flex-col flex-1">
        {tag && (
          <Link
            to={`/blog?tag=${tag.slug}`}
            className="tracked-caps text-brand hover:opacity-75 transition-opacity mb-2 block"
            onClick={(e) => e.stopPropagation()}
          >
            {tag.name}
          </Link>
        )}
        <h2 className="font-serif-display text-xl leading-snug mb-3 group-hover:text-brand transition-colors">
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        </h2>
        {post.excerpt && (
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4 flex-1">
            {post.excerpt}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="text-xs text-muted-foreground/70">
            {formatDate(post.published_at)}
          </span>
          {post.reading_time > 0 && (
            <span className="text-xs text-muted-foreground/70">
              {post.reading_time} min read
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function BlogIndex() {
  useSEO({
    title: "Blog — AI Knowledge Governance — ProofMark",
    description:
      "Thinking on enterprise AI knowledge governance, contributor classification, evidence trails, and revocation workflows. By the ProofMark team.",
    path: "/blog",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag");

  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getPosts({ tag: activeTag || undefined }),
      getTags(),
    ])
      .then(([{ posts: fetchedPosts }, fetchedTags]) => {
        setPosts(fetchedPosts);
        setTags(fetchedTags.filter((t) => (t.count?.posts || 0) > 0));
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load posts. Please try again.");
        setLoading(false);
      });
  }, [activeTag]);

  // ItemList schema — injected after posts load, cleaned up on unmount / re-filter
  useEffect(() => {
    if (posts.length === 0) return;
    const BASE = "https://proofmark-frontend-production.up.railway.app";
    const scriptId = "proofmark-jsonld-blog-list";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "ProofMark Blog",
      url: `${BASE}/blog`,
      itemListElement: posts.map((post, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: post.title,
        url: `${BASE}/blog/${post.slug}`,
      })),
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [posts]);

  function setTag(slug) {
    if (slug) setSearchParams({ tag: slug });
    else setSearchParams({});
  }

  return (
    <BlogLayout>
      {/* Header */}
      <div className="mb-10">
        <span className="tracked-caps">ProofMark Blog</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">
          AI Knowledge Governance
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
          Thinking on governance frameworks, evidence trails, contributor classification,
          and the patterns that let enterprises trust their AI systems.
        </p>
      </div>

      {/* Tag filter — only shown when there are multiple tags */}
      {tags.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setTag(null)}
            className={`text-sm px-3 py-1 rounded-full border transition-colors ${
              !activeTag
                ? "border-brand bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:border-brand/40"
            }`}
          >
            All
          </button>
          {tags.map((tag) => (
            <button
              key={tag.slug}
              onClick={() => setTag(activeTag === tag.slug ? null : tag.slug)}
              className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                activeTag === tag.slug
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-border text-muted-foreground hover:border-brand/40"
              }`}
            >
              {tag.name}
              {tag.count?.posts ? (
                <span className="ml-1 opacity-50">{tag.count.posts}</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>
      )}
      {!loading && error && (
        <div className="py-24 text-center text-sm text-rose-600">{error}</div>
      )}
      {!loading && !error && posts.length === 0 && (
        <div className="py-24 text-center text-sm text-muted-foreground">
          {activeTag ? "No posts with this tag yet." : "No posts yet — check back soon."}
        </div>
      )}

      {/* Post grid */}
      {!loading && !error && posts.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-6 mb-16">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      )}

      {/* Subscribe banner */}
      <div className="border border-brand/20 rounded-lg bg-brand/5 px-8 py-7">
        <h2 className="font-serif-display text-xl mb-1">
          Stay current on AI governance
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          New posts on governance frameworks, review workflows, and compliance patterns —
          directly to your inbox.
        </p>
        <GhostSubscribeForm />
      </div>
    </BlogLayout>
  );
}
